import { z } from 'zod';
import { AppError } from './errors';

export type JsonPrimitive = string | number | boolean | null;
export type Json = JsonPrimitive | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

export const JsonValue: z.ZodType<Json> = z.json();

/**
 * The value of a `json`/`jsonb` column as it left the database: node-postgres hands JSON back
 * parsed, but text copies of it (raw SQL casts, older rows, other drivers) arrive as a string.
 * Not for JSON columns that hold a bare string; parse those with their schema directly.
 */
export function jsonColumnValue(value: unknown): unknown {
  return typeof value === 'string' ? (JSON.parse(value) as unknown) : value;
}

/**
 * A JSON column read through its schema (data stored by an older version, or edited by hand,
 * must not travel on as if it had the current shape). Throws `internal` naming the column when
 * the stored value does not match; use {@link readJsonColumnOr} where a fallback is safe.
 */
export function readJsonColumn<S extends z.ZodType>(schema: S, value: unknown, column: string): z.output<S> {
  const r = schema.safeParse(jsonColumnValue(value));
  if (r.success) return r.data;
  throw new AppError('internal', `Stored ${column} does not have the expected shape`, {
    column,
    issues: r.error.issues.map((i) => ({ path: i.path.map(String).join('.'), message: i.message })),
  });
}

/** A JSON column read through its schema, or `fallback` when it is empty, not JSON or of another shape. */
export function readJsonColumnOr<S extends z.ZodType, F>(
  schema: S,
  value: unknown,
  fallback: F,
): z.output<S> | F {
  if (value == null) return fallback;
  let json: unknown;
  try {
    json = jsonColumnValue(value);
  } catch {
    return fallback;
  }
  const r = schema.safeParse(json);
  return r.success ? r.data : fallback;
}

/** Text parsed as JSON and checked by `schema`; `null` when it is not JSON or of another shape. */
export function parseJsonText<S extends z.ZodType>(schema: S, text: string): z.output<S> | null {
  let json: unknown;
  try {
    json = JSON.parse(text) as unknown;
  } catch {
    return null;
  }
  const r = schema.safeParse(json);
  return r.success ? r.data : null;
}

/** A non-null, non-array object, readable key by key (parsed JSON, a config, a tool's input). */
export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An own property of an object by name (a typed DTO read generically, e.g. for a diff); undefined when absent or `value` is no object. */
export function propertyOf(value: unknown, key: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined;
  return Object.prototype.hasOwnProperty.call(value, key) ? (Reflect.get(value, key) as unknown) : undefined;
}
